import * as React from 'react'
import { useState, useContext, useCallback, useRef } from 'react'
import { callZapier } from 'utils/zapier/callZapier'
import { IntakeContext } from 'context/IntakeContext'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import SavingsIcon from '@mui/icons-material/Savings'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import HistoryEduIcon from '@mui/icons-material/HistoryEdu'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Slide from '@mui/material/Slide'
import CloseIcon from '@mui/icons-material/Close'
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined'

import { ModalOnboarding } from 'layouts/onboarding'
import { steps } from 'layouts/onboarding'
import { toast } from 'react-toastify'
import { useFormDebounce } from 'hooks/useFormDebounce'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material'

function createData(icon, description, link) {
  return { icon, description, link }
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const rows = [
  createData(
    <SavingsIcon fontSize="medium" />,
    'Open a Roth IRA',
    `Let's do it!`
  ),
  createData(
    <AutoGraphIcon fontSize="medium" />,
    'Contribute at least 5% to your TSP',
    `Let's do it!`
  ),
  createData(
    <AddCircleIcon fontSize="medium" />,
    'Begin Contributing to your TSP',
    `Let's do it!`
  ),
  createData(
    <HistoryEduIcon fontSize="medium" />,
    'Set up a Will',
    `Let's do it!`
  ),
]

export default function TaskRecommendations({ userTasks = {}, userAssets }) {
  // console.log(userTasks)
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [openDialogModal, setOpenDialogModal] = useState(false)
  const [openWillModal, setOpenWillModal] = useState(false)
  const [showModalOnboarding, setShowModalOnboarding] = useState(false)
  const rothAssetsCount =
    userAssets?.filter(
      ({ subTypeClassification }) => subTypeClassification === 'ira-roth'
    ).length || 0
  const remainingOnboardingSteps = useRef(
    steps.filter(
      (step) =>
        step.type === 'Question' &&
        step.tags?.includes('onboarding') &&
        !intakeState[step.props?.id] &&
        (step?.dependencies || []).every(
          ({ attribute, value }) => intakeState[attribute] === value
        )
    )
  )
  const [dialogText, setDialogText] = useState({
    header: 'A PARCO Team Member is ready to help!',
    body: 'Let us know when best to contact you so we can work on improving your Retirement Readiness!',
  })
  const improveScoreHelp = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/3zmzqpy/',
      payload: {
        user: {
          firstName: intakeState.firstName,
          lastName: intakeState.lastName,
          phoneNumber: intakeState.phone,
          email: intakeState.email,
        },
      },
      sendNotification: true,
      notificationMessage:
        'A member of the PARCO team will reach out to you shortly! Expect a call to help improve your retirement readiness.',
    })
  }
  const handleDialogModalOpen = () => {
    // Use the Defualt message
    // setDialogText({ header: header, body: body })
    setOpenDialogModal(true)
  }
  const handleRothDialogModalOpen = () => {
    setDialogText({
      header: 'A PARCO Team Member is ready to help you open a Roth IRA!',
      body: 'Let us know when best to contact you so we can work on setting up your new Roth IRA account',
    })
    setOpenDialogModal(true)
  }
  const handleDialogModalClose = () => {
    setOpenDialogModal(false)
  }
  const handleCallMeClose = () => {
    improveScoreHelp()
    setOpenDialogModal(false)
  }
  const handleScheduleCallClose = () => {
    setOpenDialogModal(false)
  }
  const handleWillModalOpen = () => {
    // setWillText({ header: header, body: body })
    setOpenWillModal(true)
  }
  const handleWillModalClose = () => {
    setOpenWillModal(false)
  }
  const handleHaveWillClose = () => {
    updateIntakeStateAndSubmit(() => {
      const newState = updateIntakeStateAtKey({ hasWill: true })
      toast.info('You have successfully completed setting up a will', {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      })
      setOpenWillModal(false)
      return newState
    })()
  }
  const handleCreateWillClose = () => {
    setOpenWillModal(false)
  }
  const closeModalOnboarding = useCallback(() => {
    setShowModalOnboarding(false)
    // updating this ref ONLY works because the setShowModalOnboarding
    // updates state and triggers a rerender, which passes this into the
    // modal onboarding component
    remainingOnboardingSteps.current = steps.filter(
      (step) =>
        step.type === 'Question' &&
        step.tags?.includes('onboarding') &&
        !intakeState[step.props?.id] &&
        (step?.dependencies || []).every(
          ({ attribute, value }) => intakeState[attribute] === value
        )
    )
  }, [intakeState])

  const openModalOnboarding = useCallback(() => {
    setShowModalOnboarding(true)
  }, [])

  return (
    <>
      <ModalOnboarding
        open={showModalOnboarding}
        handleClose={closeModalOnboarding}
        modalHeader={'Complete Onboarding'}
        steps={remainingOnboardingSteps.current}
      />
      <Dialog
        open={openDialogModal}
        TransitionComponent={Transition}
        onClose={handleDialogModalClose}
        aria-labelledby="improve-score-dialog-title"
        aria-describedby="improve-score-dialog-description"
      >
        <DialogTitle
          id="improve-score-dialog-title"
          sx={{ paddingRight: '40px' }}
        >
          {dialogText.header}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleDialogModalClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#344767',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ paddingTop: '0' }}>
          <DialogContentText id="improve-score-dialog-description">
            {dialogText.body}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            rel="noreferrer"
            size="large"
            variant="contained"
            sx={[
              {
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                marginBottom: { xs: '8px', sm: '0px' },
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            onClick={handleCallMeClose}
          >
            CALL ME NOW
          </Button>
          <Button
            rel="noreferrer"
            size="large"
            variant="contained"
            sx={[
              {
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            href="https://team_parco.apptoto.com/"
            target="_blank"
            onClick={handleScheduleCallClose}
          >
            SCHEDULE A CALL
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openWillModal}
        TransitionComponent={Transition}
        onClose={handleWillModalClose}
        aria-labelledby="will-dialog-title"
        aria-describedby="will-dialog-description"
      >
        <DialogTitle id="will-dialog-title" sx={{ paddingRight: '40px' }}>
          Already Have a Will or Need Help Creating One?
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleWillModalClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#344767',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ paddingTop: '0' }}>
          <DialogContentText id="will-dialog-description">
            Our partners at Trust & Will are experts in will creation and are
            ready to help you get the process started. Use the button below to
            begin or let us know you already have a will!
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            rel="noreferrer"
            size="large"
            variant="contained"
            sx={[
              {
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            href="https://trustandwill.com/advisors/invite/ktviem01?utm_medium=affiliate&utm_source=impact&utm_campaign=2736293&utm_content=w-wSXa2fNxyKRY4xwczFPTbCUkCxjNxChVHT0o0&utm_name&utm_term&irgwc=1&ir_partnerid=2736293&ir_adid=1189778&ir_campaignid=11883"
            target="_blank"
            onClick={handleCreateWillClose}
          >
            CREATE A WILL
          </Button>
          <Button
            rel="noreferrer"
            size="large"
            variant="contained"
            sx={[
              {
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                marginBottom: { xs: '8px', sm: '0px' },
              },
              {
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              },
            ]}
            onClick={handleHaveWillClose}
            // onClick={updateIntakeStateAndSubmit(() =>
            //   updateIntakeStateAtKey({ hasWill: true })
            // )}
          >
            I Have A Will
          </Button>
        </DialogActions>
      </Dialog>
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 1, boxShadow: 0, border: '1px solid #ddd' }}
      >
        <Table aria-label="Task Recommendations" size="small">
          <TableBody
            sx={{
              'tr:last-child td, tr:last-child th': { border: 0 },
              'tr>td:first-of-type': {
                padding: '10px 0px 4px 16px',
                width: '1%',
              },
              'tr>td': {
                fontSize: '18px',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell>
                <BallotOutlinedIcon fontSize="medium" />
              </TableCell>
              <TableCell align="left">Complete Onboarding</TableCell>
              <TableCell align="right">
                {!intakeState?.estimatedSocialSecurity ? (
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      fontSize: '16px',
                      color: '#3498DB',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 400,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#3498DB',
                      },
                    }}
                    onClick={openModalOnboarding}
                  >
                    Let's Do It!
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    endIcon={<CheckCircleIcon />}
                    sx={{
                      fontSize: '16px',
                      color: '#344767',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 500,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#344767',
                        cursor: 'default',
                      },
                    }}
                  >
                    Completed!
                  </Button>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <SavingsIcon fontSize="medium" />
              </TableCell>
              <TableCell align="left">Open a Roth IRA</TableCell>
              <TableCell align="right">
                {rothAssetsCount > 0 ? (
                  <Button
                    variant="text"
                    endIcon={<CheckCircleIcon />}
                    sx={{
                      fontSize: '16px',
                      color: '#344767',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 500,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#344767',
                        cursor: 'default',
                      },
                    }}
                  >
                    Completed!
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      fontSize: '16px',
                      color: '#3498DB',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 400,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#3498DB',
                      },
                    }}
                    onClick={handleRothDialogModalOpen}
                  >
                    Let's Do It!
                  </Button>
                )}
              </TableCell>
            </TableRow>
            {Object.keys(userTasks).map((key) => (
              <TableRow key={key.id}>
                <TableCell>
                  <AutoGraphIcon fontSize="medium" />
                </TableCell>
                <TableCell align="left">{userTasks[key].description}</TableCell>
                <TableCell align="right">
                  {userTasks[key].completedAt === null ? (
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        fontSize: '16px',
                        color: '#3498DB',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        textTransform: 'capitalize',
                        fontWeight: 400,
                        '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                          transform: 'none',
                          boxShadow: 'none',
                          color: '#3498DB',
                        },
                      }}
                      onClick={handleDialogModalOpen}
                    >
                      Let's Do It!
                    </Button>
                  ) : (
                    <Button
                      variant="text"
                      endIcon={<CheckCircleIcon />}
                      sx={{
                        fontSize: '16px',
                        color: '#344767',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                          transform: 'none',
                          boxShadow: 'none',
                          color: '#344767',
                          cursor: 'default',
                        },
                      }}
                    >
                      Completed!
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <HistoryEduIcon fontSize="medium" />
              </TableCell>
              <TableCell align="left">Set Up a Will</TableCell>
              <TableCell align="right">
                {!intakeState?.hasWill ? (
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      fontSize: '16px',
                      color: '#3498DB',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 400,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#3498DB',
                      },
                    }}
                    onClick={handleWillModalOpen}
                  >
                    Let's Do It!
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    endIcon={<CheckCircleIcon />}
                    sx={{
                      fontSize: '16px',
                      color: '#344767',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      textTransform: 'capitalize',
                      fontWeight: 500,
                      '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                        transform: 'none',
                        boxShadow: 'none',
                        color: '#344767',
                        cursor: 'default',
                      },
                    }}
                  >
                    Completed!
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
